import { PrimaryGeneratedColumn, Column, OneToMany, Entity, UpdateDateColumn, ManyToOne, CreateDateColumn } from 'typeorm';

const APP_CRITICALITIES = {
    VERY_LOW: "Very Low",
    LOW: "Low",
    MEDIUM: "Medium",
    HIGH: "High",
    VERY_HIGH: "Very High",
    NOT_APPLICABLE: "n/a",
};

// Where a tracking item's master data record itself came from: created
// manually by an OE user through the app, or brought in by the periodic
// distribution import. Distinct from FormQuestion.source, which tracks the
// origin of an individual questionnaire answer.
const APP_ITEM_SOURCES = {
    MANUAL: "Manual",
    IMPORT: "Import",
};

const APP_LIFE_CYCLES = {
    ACTIVE: "Active",
    IN_PRODUCTION: "In production",
    IN_DEVELOPMENT: "In development",
    PLANNED: "Planned",
    RETIRED: "Retired",
};

const APP_RECORD_TYPES = {
    BUSINESS_APPLICATION: "Business Application",
    IT_SERVICE: "IT Service",
};
/**
 * Record types kept in the database and in the Postgres enum, but excluded from
 * every calculation, table, chart, filter and export. Empty this array to bring
 * a type back; never remove the value from APP_RECORD_TYPES, because that drives
 * the enum column and existing rows would block the synchronize.
 */
const HIDDEN_APP_RECORD_TYPES = [
    APP_RECORD_TYPES.IT_SERVICE,
];
const VISIBLE_APP_RECORD_TYPES = Object.values(APP_RECORD_TYPES).filter((recordType) => !HIDDEN_APP_RECORD_TYPES.includes(recordType));
const HIDDEN_RECORD_TYPE_KEYS = new Set(HIDDEN_APP_RECORD_TYPES.map((recordType) => recordType.toLowerCase()));
// Stored values are not normalized, so compare trimmed and lowercased.
const isHiddenRecordType = (value) => {
    const normalized = String(value ?? "")
        .trim()
        .toLowerCase();
    return normalized.length > 0 && HIDDEN_RECORD_TYPE_KEYS.has(normalized);
};

const APP_SOURCES = {
    LLM_SUGGESTED: "LLM Suggested",
    OE_DATA: "OE Data",
    ARCHER: "Archer",
    CMDB: "CMDB",
    ADONIS: "Adonis",
    ONE_TRUST: "OneTrust",
    ADOIT: "ADOIT",
};

const APPLICATION_CAPABILITIES = {
    CRM: "CRM / Sales Management",
    DWP: "Digital Workplace / Sales Workplace",
    PRT: "Portal / Frontend",
    QTB: "Quote / Bind Engine",
    UWE: "Underwriting Engine",
    PAS: "Policy Administration System",
    CMS: "Commission Management System",
    IAM: "Identity / Access Management",
    API: "API / Integration Layer",
    DOC: "Document / Signature / Workflow",
    DAT: "Data / Reporting / Analytics",
    CLM: "Claims / Assistance Platform",
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const stringArrayColumn = () => ({
    type: "simple-array",
    nullable: true,
    transformer: {
        to: (value) => value,
        from: (value) => Array.isArray(value) ? value.filter(Boolean) : value,
    },
});
let BasicItem = class BasicItem {
    id;
    customId;
    name;
    description;
    owner;
    startDate;
    endDate;
    dropdownFieldOne;
    recordType;
    criticality;
    lifeCycle;
    hasContracts;
    otherDistributionChannels;
    processSteps;
    applicationCapabilities;
    technicalExposure;
    budget;
    projectSpecificItem;
    itemAssignedToAllProjects;
    // Where the record itself came from: created manually by an OE user, or
    // brought in by the distribution import. Lets a manually-created item's
    // creator bypass the normal OE read-only restrictions on their own item.
    source;
    // Email of the user who manually created this item (unset for imported
    // items). Paired with `source === "Manual"` to grant the creator full
    // edit/delete rights over their own item. Also used to look up the
    // creator's display name on demand (via Keycloak) for the review dialog.
    createdBy;
    reportingItems;
    projectIds;
    flag;
};
__decorate([
    PrimaryGeneratedColumn(),
    __metadata("design:type", String)
], BasicItem.prototype, "id", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], BasicItem.prototype, "customId", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], BasicItem.prototype, "name", void 0);
__decorate([
    Column({ type: "text", nullable: true }),
    __metadata("design:type", String)
], BasicItem.prototype, "description", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], BasicItem.prototype, "owner", void 0);
__decorate([
    Column({ type: "date", nullable: true }),
    __metadata("design:type", String)
], BasicItem.prototype, "startDate", void 0);
__decorate([
    Column({ type: "date", nullable: true }),
    __metadata("design:type", String)
], BasicItem.prototype, "endDate", void 0);
__decorate([
    Column({ type: "text", nullable: true }),
    __metadata("design:type", String)
], BasicItem.prototype, "dropdownFieldOne", void 0);
__decorate([
    Column({
        type: "enum",
        enum: Object.values(APP_RECORD_TYPES),
        nullable: true,
    }),
    __metadata("design:type", String)
], BasicItem.prototype, "recordType", void 0);
__decorate([
    Column({
        type: "enum",
        enum: Object.values(APP_CRITICALITIES),
        nullable: true,
    }),
    __metadata("design:type", String)
], BasicItem.prototype, "criticality", void 0);
__decorate([
    Column({
        type: "enum",
        enum: Object.values(APP_LIFE_CYCLES),
        nullable: true,
    }),
    __metadata("design:type", String)
], BasicItem.prototype, "lifeCycle", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], BasicItem.prototype, "hasContracts", void 0);
__decorate([
    Column(stringArrayColumn()),
    __metadata("design:type", Array)
], BasicItem.prototype, "otherDistributionChannels", void 0);
__decorate([
    Column(stringArrayColumn()),
    __metadata("design:type", Array)
], BasicItem.prototype, "processSteps", void 0);
__decorate([
    Column(stringArrayColumn()),
    __metadata("design:type", Array)
], BasicItem.prototype, "applicationCapabilities", void 0);
__decorate([
    Column(stringArrayColumn()),
    __metadata("design:type", Array)
], BasicItem.prototype, "technicalExposure", void 0);
__decorate([
    Column({ type: "decimal", precision: 10, scale: 2, nullable: true }),
    __metadata("design:type", Number)
], BasicItem.prototype, "budget", void 0);
__decorate([
    Column({ type: "boolean", nullable: true }),
    __metadata("design:type", Boolean)
], BasicItem.prototype, "projectSpecificItem", void 0);
__decorate([
    Column({ type: "boolean", nullable: true }),
    __metadata("design:type", Boolean)
], BasicItem.prototype, "itemAssignedToAllProjects", void 0);
__decorate([
    Column({
        type: "enum",
        enum: Object.values(APP_ITEM_SOURCES),
        nullable: true,
    }),
    __metadata("design:type", String)
], BasicItem.prototype, "source", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], BasicItem.prototype, "createdBy", void 0);
__decorate([
    OneToMany(() => ReportingItem, (reportingItem) => reportingItem.basicItem, {
        cascade: true,
        orphanedRowAction: "delete",
    }),
    __metadata("design:type", Array)
], BasicItem.prototype, "reportingItems", void 0);
BasicItem = __decorate([
    Entity()
], BasicItem);

const PROJECT_NAMES = [
    {
        single: "Project",
        plural: "Projects",
    },
    {
        single: "Measure",
        plural: "Measures",
    },
    {
        single: "Finding",
        plural: "Findings",
    },
    {
        single: "Company",
        plural: "Companies",
    },
    {
        single: "Subsidiary",
        plural: "Subsidiaries",
    },
    {
        single: "Department",
        plural: "Departments",
    },
    {
        single: "OE",
        plural: "OEs",
    },
];
const ITEM_NAMES = [
    { single: "Tracking Item", plural: "Tracking Items" },
    {
        single: "Deliverable",
        plural: "Deliverables",
    },
    {
        single: "Task",
        plural: "Tasks",
    },
    {
        single: "Activity",
        plural: "Activities",
    },
    {
        single: "Milestone",
        plural: "Milestones",
    },
    {
        single: "Measure",
        plural: "Measures",
    },
    {
        single: "Finding",
        plural: "Findings",
    },
    {
        single: "Initiative",
        plural: "Initiatives",
    },
    { single: "Question", plural: "Questions" },
    { single: "DC System", plural: "DC Systems" },
];

let Configuration = class Configuration {
    id;
    isPublished;
    programName;
    programOwner;
    logo;
    programDescription;
    projectName;
    itemName;
    completedConfigurationSteps;
    useCase;
    type;
    canProgramTeamCreateProjects;
    canProgramTeamCreateItems;
    canReportingTeamCreateItems;
    trackingItemsApplyToAllProjects;
    fieldsDefinition;
    statusDefinition;
    statusReportingLevel;
    degreeOfCompletionDefinition;
    categoryLabel;
    categoryDefinition;
    secondCategoryDefinition;
    dropdownFieldOneDefinition;
    answerDefinition;
    distributionChannelDefinition;
    dcRelevanceDefinition;
    trackingItemForms;
    semaphoreStatusDefinition;
    reportingCycle;
    updatedAt;
    publishedAt;
    isReportingEnabled;
    homePortalAppId;
    customFields;
    client;
    tool;
};
__decorate([
    PrimaryGeneratedColumn(),
    __metadata("design:type", String)
], Configuration.prototype, "id", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", Boolean)
], Configuration.prototype, "isPublished", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], Configuration.prototype, "programName", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], Configuration.prototype, "programOwner", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], Configuration.prototype, "logo", void 0);
__decorate([
    Column({ type: "text", nullable: true }),
    __metadata("design:type", String)
], Configuration.prototype, "programDescription", void 0);
__decorate([
    Column("enum", {
        enum: PROJECT_NAMES.map((n) => n.single),
        nullable: true,
    }),
    __metadata("design:type", String)
], Configuration.prototype, "projectName", void 0);
__decorate([
    Column("enum", {
        enum: ITEM_NAMES.map((n) => n.single),
        nullable: true,
    }),
    __metadata("design:type", String)
], Configuration.prototype, "itemName", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], Configuration.prototype, "completedConfigurationSteps", void 0);
__decorate([
    Column("enum", {
        enum: ["pmo", "rollout", "audit", "assessment", "distribution"],
        nullable: true,
    }),
    __metadata("design:type", String)
], Configuration.prototype, "useCase", void 0);
__decorate([
    Column("enum", {
        enum: ["project", "item", "reporting", "general"],
        nullable: true,
    }),
    __metadata("design:type", String)
], Configuration.prototype, "type", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", Boolean)
], Configuration.prototype, "canProgramTeamCreateProjects", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", Boolean)
], Configuration.prototype, "canProgramTeamCreateItems", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", Boolean)
], Configuration.prototype, "canReportingTeamCreateItems", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", Boolean)
], Configuration.prototype, "trackingItemsApplyToAllProjects", void 0);
__decorate([
    Column({ type: "jsonb", nullable: true }),
    __metadata("design:type", Array)
], Configuration.prototype, "fieldsDefinition", void 0);
__decorate([
    Column({ type: "jsonb", nullable: true }),
    __metadata("design:type", Array)
], Configuration.prototype, "statusDefinition", void 0);
__decorate([
    Column("enum", {
        enum: ["overall", "category"],
        nullable: true,
    }),
    __metadata("design:type", String)
], Configuration.prototype, "statusReportingLevel", void 0);
__decorate([
    Column({ type: "jsonb", nullable: true }),
    __metadata("design:type", Array)
], Configuration.prototype, "degreeOfCompletionDefinition", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], Configuration.prototype, "categoryLabel", void 0);
__decorate([
    Column({ type: "jsonb", nullable: true }),
    __metadata("design:type", Array)
], Configuration.prototype, "categoryDefinition", void 0);
__decorate([
    Column({ type: "jsonb", nullable: true }),
    __metadata("design:type", Array)
], Configuration.prototype, "secondCategoryDefinition", void 0);
__decorate([
    Column({ type: "jsonb", nullable: true }),
    __metadata("design:type", Array)
], Configuration.prototype, "dropdownFieldOneDefinition", void 0);
__decorate([
    Column({ type: "jsonb", nullable: true }),
    __metadata("design:type", Array)
], Configuration.prototype, "answerDefinition", void 0);
__decorate([
    Column({ type: "jsonb", nullable: true }),
    __metadata("design:type", Array)
], Configuration.prototype, "distributionChannelDefinition", void 0);
__decorate([
    Column({ type: "jsonb", nullable: true }),
    __metadata("design:type", Array)
], Configuration.prototype, "dcRelevanceDefinition", void 0);
__decorate([
    Column({ type: "jsonb", nullable: true }),
    __metadata("design:type", Array)
], Configuration.prototype, "trackingItemForms", void 0);
__decorate([
    Column({ type: "jsonb", nullable: true }),
    __metadata("design:type", Array)
], Configuration.prototype, "semaphoreStatusDefinition", void 0);
__decorate([
    Column({ type: "jsonb", nullable: true }),
    __metadata("design:type", Object)
], Configuration.prototype, "reportingCycle", void 0);
__decorate([
    UpdateDateColumn({ type: "timestamp", nullable: true }),
    __metadata("design:type", Date)
], Configuration.prototype, "updatedAt", void 0);
__decorate([
    Column({ type: "timestamp", nullable: true }),
    __metadata("design:type", Date)
], Configuration.prototype, "publishedAt", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", Boolean)
], Configuration.prototype, "isReportingEnabled", void 0);
__decorate([
    Column({ type: "text", nullable: true }),
    __metadata("design:type", String)
], Configuration.prototype, "homePortalAppId", void 0);
__decorate([
    OneToMany(() => CustomFieldDefinition, (customField) => customField.configuration, {}),
    __metadata("design:type", Array)
], Configuration.prototype, "customFields", void 0);
Configuration = __decorate([
    Entity()
], Configuration);

let CustomFieldDefinition = class CustomFieldDefinition {
    id;
    label;
    type;
    options;
    purpose;
    enabled;
    mandatory;
    customFieldValues;
    configuration;
    flag;
};
__decorate([
    PrimaryGeneratedColumn(),
    __metadata("design:type", String)
], CustomFieldDefinition.prototype, "id", void 0);
__decorate([
    Column(),
    __metadata("design:type", String)
], CustomFieldDefinition.prototype, "label", void 0);
__decorate([
    Column(),
    __metadata("design:type", String)
], CustomFieldDefinition.prototype, "type", void 0);
__decorate([
    Column({ type: "jsonb", nullable: true }),
    __metadata("design:type", Array)
], CustomFieldDefinition.prototype, "options", void 0);
__decorate([
    Column({
        type: "enum",
        enum: ["itemBasic", "itemReporting", "projectBasic", "projectReporting"],
        nullable: true,
    }),
    __metadata("design:type", String)
], CustomFieldDefinition.prototype, "purpose", void 0);
__decorate([
    Column({ default: true }),
    __metadata("design:type", Boolean)
], CustomFieldDefinition.prototype, "enabled", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", Boolean)
], CustomFieldDefinition.prototype, "mandatory", void 0);
__decorate([
    OneToMany(() => CustomFieldValue, (customFieldValue) => customFieldValue.customFieldDefinition, {
        cascade: true,
    }),
    __metadata("design:type", Array)
], CustomFieldDefinition.prototype, "customFieldValues", void 0);
__decorate([
    ManyToOne(() => Configuration, (configuration) => configuration.customFields, {
        onDelete: "CASCADE",
    }),
    __metadata("design:type", Configuration)
], CustomFieldDefinition.prototype, "configuration", void 0);
CustomFieldDefinition = __decorate([
    Entity()
], CustomFieldDefinition);

let Project = class Project {
    id;
    name;
    description;
    startDate;
    endDate;
    owner;
    le;
    customId;
    category;
    secondCategory;
    freeTextFieldOne;
    userCreatedProject;
    approvedBudget;
    spentBudget;
    managementSummary;
    challenges;
    achievements;
    nextSteps;
    distributionChannels;
    reportingItems;
    customFields;
    createdAt;
    updatedAt;
    flag;
    _calculated;
};
__decorate([
    PrimaryGeneratedColumn(),
    __metadata("design:type", String)
], Project.prototype, "id", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], Project.prototype, "name", void 0);
__decorate([
    Column({ type: "text", nullable: true }),
    __metadata("design:type", String)
], Project.prototype, "description", void 0);
__decorate([
    Column({ type: "date", nullable: true }),
    __metadata("design:type", String)
], Project.prototype, "startDate", void 0);
__decorate([
    Column({ type: "date", nullable: true }),
    __metadata("design:type", String)
], Project.prototype, "endDate", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], Project.prototype, "owner", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], Project.prototype, "le", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], Project.prototype, "customId", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], Project.prototype, "category", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], Project.prototype, "secondCategory", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], Project.prototype, "freeTextFieldOne", void 0);
__decorate([
    Column({ type: "boolean", nullable: true }),
    __metadata("design:type", Boolean)
], Project.prototype, "userCreatedProject", void 0);
__decorate([
    Column({
        type: "decimal",
        precision: 10,
        scale: 2,
        nullable: true,
        transformer: {
            to: (val) => (val === "" || !val ? 0 : val),
            from: (val) => parseFloat(val ?? "0"),
        },
    }),
    __metadata("design:type", Number)
], Project.prototype, "approvedBudget", void 0);
__decorate([
    Column({
        type: "decimal",
        precision: 10,
        scale: 2,
        nullable: true,
        transformer: {
            to: (val) => (val === "" || !val ? 0 : val),
            from: (val) => parseFloat(val ?? "0"),
        },
    }),
    __metadata("design:type", Number)
], Project.prototype, "spentBudget", void 0);
__decorate([
    Column({ type: "text", nullable: true }),
    __metadata("design:type", String)
], Project.prototype, "managementSummary", void 0);
__decorate([
    Column({ type: "text", nullable: true }),
    __metadata("design:type", String)
], Project.prototype, "challenges", void 0);
__decorate([
    Column({ type: "text", nullable: true }),
    __metadata("design:type", String)
], Project.prototype, "achievements", void 0);
__decorate([
    Column({ type: "text", nullable: true }),
    __metadata("design:type", String)
], Project.prototype, "nextSteps", void 0);
__decorate([
    Column({
        type: "simple-array",
        nullable: true,
        // TypeORM hydrates an empty simple-array as [""]; strip blanks so "no channels" stays empty.
        transformer: {
            to: (value) => value,
            from: (value) => Array.isArray(value) ? value.filter(Boolean) : value,
        },
    }),
    __metadata("design:type", Array)
], Project.prototype, "distributionChannels", void 0);
__decorate([
    OneToMany(() => ReportingItem, (reportingItem) => reportingItem.project, {
        cascade: true,
        eager: true,
        orphanedRowAction: "delete",
    }),
    __metadata("design:type", Array)
], Project.prototype, "reportingItems", void 0);
__decorate([
    OneToMany(() => CustomFieldValue, (customFieldValue) => customFieldValue.project, {}),
    __metadata("design:type", Array)
], Project.prototype, "customFields", void 0);
__decorate([
    CreateDateColumn(),
    __metadata("design:type", Date)
], Project.prototype, "createdAt", void 0);
__decorate([
    UpdateDateColumn(),
    __metadata("design:type", Date)
], Project.prototype, "updatedAt", void 0);
Project = __decorate([
    Entity()
], Project);

let CustomFieldValue = class CustomFieldValue {
    id;
    value;
    customFieldDefinition;
    reportingItem;
    project;
};
__decorate([
    PrimaryGeneratedColumn(),
    __metadata("design:type", String)
], CustomFieldValue.prototype, "id", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], CustomFieldValue.prototype, "value", void 0);
__decorate([
    ManyToOne(() => CustomFieldDefinition, (customFieldDefinition) => customFieldDefinition.customFieldValues, { onDelete: "CASCADE" }),
    __metadata("design:type", CustomFieldDefinition)
], CustomFieldValue.prototype, "customFieldDefinition", void 0);
__decorate([
    ManyToOne(() => ReportingItem, (reportingItem) => reportingItem.customFields, { onDelete: "CASCADE" }),
    __metadata("design:type", Object)
], CustomFieldValue.prototype, "reportingItem", void 0);
__decorate([
    ManyToOne(() => Project, (project) => project.customFields, {
        onDelete: "CASCADE",
    }),
    __metadata("design:type", Project)
], CustomFieldValue.prototype, "project", void 0);
CustomFieldValue = __decorate([
    Entity()
], CustomFieldValue);

let ReportingItem = class ReportingItem {
    id;
    customId;
    completionDate;
    availableBudget;
    spentBudget;
    status;
    semaphoreStatus1;
    semaphoreStatus2;
    semaphoreStatus3;
    progress;
    degreeOfCompletion;
    lockedBlockIds;
    comment;
    distributionChannel;
    dcRelevance;
    /** When the ADOIT import last modified dcRelevance; null if it never did. */
    dcRelevanceModifiedImportedAt;
    isChannelHeader;
    formData;
    llmSuggestedFormData;
    attachments;
    customFields;
    severity;
    lastUpdatedAt;
    lastUpdatedBy;
    project;
    basicItem;
    _calculated;
};
__decorate([
    PrimaryGeneratedColumn(),
    __metadata("design:type", String)
], ReportingItem.prototype, "id", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], ReportingItem.prototype, "customId", void 0);
__decorate([
    Column({ type: "timestamp", nullable: true }),
    __metadata("design:type", String)
], ReportingItem.prototype, "completionDate", void 0);
__decorate([
    Column({
        type: "decimal",
        precision: 10,
        scale: 2,
        nullable: true,
        transformer: {
            to: (val) => (val === "" || !val ? 0 : val),
            from: (val) => parseFloat(val ?? "0"),
        },
    }),
    __metadata("design:type", Number)
], ReportingItem.prototype, "availableBudget", void 0);
__decorate([
    Column({
        type: "decimal",
        precision: 10,
        scale: 2,
        nullable: true,
        transformer: {
            to: (val) => (val === "" || !val ? 0 : val),
            from: (val) => parseFloat(val ?? "0"),
        },
    }),
    __metadata("design:type", Number)
], ReportingItem.prototype, "spentBudget", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], ReportingItem.prototype, "status", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], ReportingItem.prototype, "semaphoreStatus1", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], ReportingItem.prototype, "semaphoreStatus2", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], ReportingItem.prototype, "semaphoreStatus3", void 0);
__decorate([
    Column({
        type: "decimal",
        precision: 5,
        scale: 2,
        nullable: true,
        transformer: {
            to: (val) => (val === "" || !val ? 0 : val),
            from: (val) => parseFloat(val ?? "0"),
        },
    }),
    __metadata("design:type", Number)
], ReportingItem.prototype, "progress", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], ReportingItem.prototype, "degreeOfCompletion", void 0);
__decorate([
    Column({ type: "jsonb", nullable: true }),
    __metadata("design:type", Array)
], ReportingItem.prototype, "lockedBlockIds", void 0);
__decorate([
    Column({ type: "text", nullable: true }),
    __metadata("design:type", String)
], ReportingItem.prototype, "comment", void 0);
__decorate([
    Column({ type: "simple-array", nullable: true }),
    __metadata("design:type", Array)
], ReportingItem.prototype, "distributionChannel", void 0);
__decorate([
    Column({ type: "enum", enum: ["Yes", "No"], nullable: true }),
    __metadata("design:type", String)
], ReportingItem.prototype, "dcRelevance", void 0);
__decorate([
    Column({ type: "timestamp", nullable: true }),
    __metadata("design:type", Object)
], ReportingItem.prototype, "dcRelevanceModifiedImportedAt", void 0);
__decorate([
    Column({ type: "boolean", nullable: true }),
    __metadata("design:type", Boolean)
], ReportingItem.prototype, "isChannelHeader", void 0);
__decorate([
    Column({ type: "jsonb", nullable: true }),
    __metadata("design:type", Object)
], ReportingItem.prototype, "formData", void 0);
__decorate([
    Column({ type: "jsonb", nullable: true }),
    __metadata("design:type", Object)
], ReportingItem.prototype, "llmSuggestedFormData", void 0);
__decorate([
    OneToMany(() => Attachment, (attachment) => attachment.reportingItem, {}),
    __metadata("design:type", Array)
], ReportingItem.prototype, "attachments", void 0);
__decorate([
    OneToMany(() => CustomFieldValue, (customFieldValue) => customFieldValue.reportingItem, {}),
    __metadata("design:type", Array)
], ReportingItem.prototype, "customFields", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], ReportingItem.prototype, "severity", void 0);
__decorate([
    Column({ type: "timestamp", nullable: true }),
    __metadata("design:type", String)
], ReportingItem.prototype, "lastUpdatedAt", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], ReportingItem.prototype, "lastUpdatedBy", void 0);
__decorate([
    ManyToOne(() => Project, (project) => project.reportingItems, {
        onDelete: "CASCADE",
    }),
    __metadata("design:type", Project)
], ReportingItem.prototype, "project", void 0);
__decorate([
    ManyToOne(() => BasicItem, (basicItem) => basicItem.reportingItems, {
        onDelete: "CASCADE",
    }),
    __metadata("design:type", BasicItem)
], ReportingItem.prototype, "basicItem", void 0);
ReportingItem = __decorate([
    Entity()
], ReportingItem);

let Attachment = class Attachment {
    id;
    fileName;
    originalName;
    filePath;
    mimeType;
    fileSize;
    comment;
    uploadedAt;
    uploadedBy;
    temporary;
    reportingItem;
};
__decorate([
    PrimaryGeneratedColumn("uuid"),
    __metadata("design:type", String)
], Attachment.prototype, "id", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], Attachment.prototype, "fileName", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], Attachment.prototype, "originalName", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], Attachment.prototype, "filePath", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], Attachment.prototype, "mimeType", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", Number)
], Attachment.prototype, "fileSize", void 0);
__decorate([
    Column({ type: "text", nullable: true }),
    __metadata("design:type", String)
], Attachment.prototype, "comment", void 0);
__decorate([
    Column({ type: "timestamp", default: () => "CURRENT_TIMESTAMP" }),
    __metadata("design:type", String)
], Attachment.prototype, "uploadedAt", void 0);
__decorate([
    Column({ nullable: true }),
    __metadata("design:type", String)
], Attachment.prototype, "uploadedBy", void 0);
__decorate([
    Column({ default: false }),
    __metadata("design:type", Boolean)
], Attachment.prototype, "temporary", void 0);
__decorate([
    ManyToOne(() => ReportingItem, (reportingItem) => reportingItem.attachments, {
        onDelete: "CASCADE",
    }),
    __metadata("design:type", ReportingItem)
], Attachment.prototype, "reportingItem", void 0);
Attachment = __decorate([
    Entity()
], Attachment);

let DashboardContent = class DashboardContent {
    id;
    managementSummary;
};
__decorate([
    PrimaryGeneratedColumn(),
    __metadata("design:type", String)
], DashboardContent.prototype, "id", void 0);
__decorate([
    Column({ type: "text", nullable: true }),
    __metadata("design:type", String)
], DashboardContent.prototype, "managementSummary", void 0);
DashboardContent = __decorate([
    Entity()
], DashboardContent);

const getDistributionChannels = (definition) => definition ?? [];
const getChannelLabel = (id, channels) => channels.find((c) => c.id === id)?.label ?? id;

const PROCESS_STEPS = {
    MKT: "Marketing / Campaign / Lead Generation",
    ADV: "Advice / Needs Analysis",
    QTE: "Quote / Tariff / Pricing",
    UWR: "Underwriting / Risk Assessment",
    BND: "Bind / Application / New Business",
    POL: "Policy Issuance / Policy Administration",
    PAY: "Payment / Billing / Collection",
    COM: "Commission / Remuneration",
    SRV: "Customer / Partner Servicing",
    CLM: "Claims / Assistance",
    REP: "Reporting / Steering / Performance",
};

const TECHNICAL_EXPOSURE = {
    PUB: "Public internet-facing",
    CUS: "Customer-facing authenticated",
    PAR: "Partner-facing authenticated",
    EXTAPI: "External / partner API",
    FED: "Federated / SSO access",
    DEV: "Partner / agency device dependent",
    INT: "Internal Allianz only",
    MFT: "File / batch / document exchange",
    UNK: "Unknown / to be validated",
};

// The only legal entity_ref values. A ref outside this table rejects the import.
const ENTITY_REF_TARGETS = {
    "basic-item.customId": {
        entity: "basicItem",
        column: "customId",
        kind: "text",
    },
    "basic-item.name": { entity: "basicItem", column: "name", kind: "text" },
    "basic-item.owner": { entity: "basicItem", column: "owner", kind: "text" },
    "basic-item.description": {
        entity: "basicItem",
        column: "description",
        kind: "longText",
    },
    "basic-item.recordType": {
        entity: "basicItem",
        column: "recordType",
        kind: "enum",
        // values stays whole: it must match the Postgres enum on the column.
        values: Object.values(APP_RECORD_TYPES),
        hiddenValues: HIDDEN_APP_RECORD_TYPES,
    },
    "basic-item.criticality": {
        entity: "basicItem",
        column: "criticality",
        kind: "enum",
        values: Object.values(APP_CRITICALITIES),
    },
    "basic-item.lifeCycle": {
        entity: "basicItem",
        column: "lifeCycle",
        kind: "enum",
        values: Object.values(APP_LIFE_CYCLES),
    },
    "basic-item.processSteps": {
        entity: "basicItem",
        column: "processSteps",
        kind: "stringArray",
        values: Object.values(PROCESS_STEPS),
    },
    "basic-item.technicalExposure": {
        entity: "basicItem",
        column: "technicalExposure",
        kind: "stringArray",
        values: Object.values(TECHNICAL_EXPOSURE),
    },
    "basic-item.applicationCapabilities": {
        entity: "basicItem",
        column: "applicationCapabilities",
        kind: "stringArray",
        values: Object.values(APPLICATION_CAPABILITIES),
    },
    "basic-item.otherDistributionChannels": {
        entity: "basicItem",
        column: "otherDistributionChannels",
        kind: "channels",
        readOnly: true,
    },
    "project.name": {
        entity: "project",
        column: "name",
        kind: "text",
        readOnly: true,
    },
    "project.customId": {
        entity: "project",
        column: "customId",
        kind: "text",
        readOnly: true,
    },
    // Editing the primary channel would leave project.distributionChannels stale,
    // so it stays read-only until that roll-up is recomputed on save.
    "reporting-item.distributionChannel": {
        entity: "reportingItem",
        column: "distributionChannel",
        kind: "channels",
        readOnly: true,
    },
    "reporting-item.degreeOfCompletion": {
        entity: "reportingItem",
        column: "degreeOfCompletion",
        kind: "configEnum",
        configSource: "degreeOfCompletionDefinition",
    },
    "reporting-item.dcRelevance": {
        entity: "reportingItem",
        column: "dcRelevance",
        kind: "enum",
        values: ["Yes", "No"],
    },
};
// `as const` is what gives EntityRef its union, but it also narrows each entry to
// its own literal shape, so an entry that omits `readOnly` or `values` does not
// declare the property at all. Read entries through here to get the full type.
const getEntityRefTarget = (ref) => ENTITY_REF_TARGETS[ref];

let ManagedUser = class ManagedUser {
    id;
    profile;
    createdAt;
    updatedAt;
};
__decorate([
    PrimaryGeneratedColumn(),
    __metadata("design:type", String)
], ManagedUser.prototype, "id", void 0);
__decorate([
    Column({ type: "jsonb" }),
    __metadata("design:type", Object)
], ManagedUser.prototype, "profile", void 0);
__decorate([
    CreateDateColumn(),
    __metadata("design:type", Date)
], ManagedUser.prototype, "createdAt", void 0);
__decorate([
    UpdateDateColumn(),
    __metadata("design:type", Date)
], ManagedUser.prototype, "updatedAt", void 0);
ManagedUser = __decorate([
    Entity()
], ManagedUser);

let TableConfiguration = class TableConfiguration {
    id;
    component;
    columns;
};
__decorate([
    PrimaryGeneratedColumn(),
    __metadata("design:type", String)
], TableConfiguration.prototype, "id", void 0);
__decorate([
    Column({ type: "text", nullable: true }),
    __metadata("design:type", String)
], TableConfiguration.prototype, "component", void 0);
__decorate([
    Column({ type: "jsonb", nullable: true }),
    __metadata("design:type", Array)
], TableConfiguration.prototype, "columns", void 0);
TableConfiguration = __decorate([
    Entity()
], TableConfiguration);

const COLORS = ["#E1F1E7", "#fef3d6", "#fae7e7"];
const CONTRAST_TEXT_COLORS = ["#008336", "#F89500", "#FF0000"];
const ICONS = [
    "mood-smile.svg",
    "mood-neutral.svg",
    "mood-sad.svg",
];

let UserSettings = class UserSettings {
    id;
    email;
    tutorialCompleted;
    setupSkipped;
    skipChangedFieldDialog;
    isSetupSkipped;
    createdAt;
    updatedAt;
};
__decorate([
    PrimaryGeneratedColumn(),
    __metadata("design:type", String)
], UserSettings.prototype, "id", void 0);
__decorate([
    Column({ unique: true }),
    __metadata("design:type", String)
], UserSettings.prototype, "email", void 0);
__decorate([
    Column({ type: "boolean", default: false }),
    __metadata("design:type", Boolean)
], UserSettings.prototype, "tutorialCompleted", void 0);
__decorate([
    Column({ type: "boolean", default: false }),
    __metadata("design:type", Boolean)
], UserSettings.prototype, "setupSkipped", void 0);
__decorate([
    Column({ type: "boolean", default: false }),
    __metadata("design:type", Boolean)
], UserSettings.prototype, "skipChangedFieldDialog", void 0);
__decorate([
    Column({ type: "boolean", default: false }),
    __metadata("design:type", Boolean)
], UserSettings.prototype, "isSetupSkipped", void 0);
__decorate([
    CreateDateColumn(),
    __metadata("design:type", Date)
], UserSettings.prototype, "createdAt", void 0);
__decorate([
    UpdateDateColumn(),
    __metadata("design:type", Date)
], UserSettings.prototype, "updatedAt", void 0);
UserSettings = __decorate([
    Entity()
], UserSettings);

let ViewConfiguration = class ViewConfiguration {
    id;
    displayTabs;
    canEdit;
    isAiEnabled;
    type;
    tabs;
};
__decorate([
    PrimaryGeneratedColumn(),
    __metadata("design:type", String)
], ViewConfiguration.prototype, "id", void 0);
__decorate([
    Column({ type: "boolean", default: false }),
    __metadata("design:type", Boolean)
], ViewConfiguration.prototype, "displayTabs", void 0);
__decorate([
    Column({ type: "boolean", default: false }),
    __metadata("design:type", Boolean)
], ViewConfiguration.prototype, "canEdit", void 0);
__decorate([
    Column({ type: "boolean", default: false }),
    __metadata("design:type", Boolean)
], ViewConfiguration.prototype, "isAiEnabled", void 0);
__decorate([
    Column({
        type: "enum",
        enum: ["dashboard", "overview", "project-common", "project-specific"],
    }),
    __metadata("design:type", String)
], ViewConfiguration.prototype, "type", void 0);
__decorate([
    Column({ type: "jsonb", nullable: true }),
    __metadata("design:type", Array)
], ViewConfiguration.prototype, "tabs", void 0);
ViewConfiguration = __decorate([
    Entity()
], ViewConfiguration);

export { APPLICATION_CAPABILITIES, APP_CRITICALITIES, APP_ITEM_SOURCES, APP_LIFE_CYCLES, APP_RECORD_TYPES, APP_SOURCES, Attachment, BasicItem, COLORS, CONTRAST_TEXT_COLORS, Configuration, CustomFieldDefinition, CustomFieldValue, DashboardContent, ENTITY_REF_TARGETS, HIDDEN_APP_RECORD_TYPES, ICONS, ITEM_NAMES, ManagedUser, PROCESS_STEPS, PROJECT_NAMES, Project, ReportingItem, TECHNICAL_EXPOSURE, TableConfiguration, UserSettings, VISIBLE_APP_RECORD_TYPES, ViewConfiguration, getChannelLabel, getDistributionChannels, getEntityRefTarget, isHiddenRecordType };
